import { useCallback, useMemo } from 'react';
import type { FilterStand } from '@xxtract/tabelfilters';
import { useStartadres, type StartadresLijst } from '@/components/tabel';
import { useLijstStandStore } from '@/stores/lijstStandStore';
import {
  DEFAULT_STILSTAND_DREMPEL_WERKDAGEN,
  PROCESSEN_FILTER_LIJST,
  PROCESSEN_PAGINAFILTER,
  processenFilterWaarden,
  type ProcessFilterValues,
} from '../procesLijsten';

const GEDEELD: StartadresLijst = { lijst: PROCESSEN_FILTER_LIJST, standSleutel: PROCESSEN_PAGINAFILTER, gedeeld: true };

/** Waarden → lijststand, vanuit de actuele stand (niet de render), zodat twee snelle wijzigingen elkaar niet wissen. */
function schrijf(patch: Partial<ProcessFilterValues>) {
  const { standen, zetPaginafilter } = useLijstStandStore.getState();
  const volgend: FilterStand = { ...standen[PROCESSEN_PAGINAFILTER]?.filters };
  const zet = (sleutel: string, waarde: FilterStand[string] | undefined) => {
    if (waarde === undefined) delete volgend[sleutel];
    else volgend[sleutel] = waarde;
  };
  if ('gln' in patch) zet('gln', patch.gln || undefined);
  if ('status' in patch) zet('status', patch.status ? [patch.status] : undefined);
  if ('stilstandDrempelWerkdagen' in patch) zet('stilstandDrempelWerkdagen', patch.stilstandDrempelWerkdagen == null ? undefined : String(patch.stilstandDrempelWerkdagen));
  if ('from' in patch || 'to' in patch) {
    const oud = volgend.periode && typeof volgend.periode === 'object' && !Array.isArray(volgend.periode) ? volgend.periode : {};
    const van = 'from' in patch ? patch.from || undefined : oud.van;
    const tot = 'to' in patch ? patch.to || undefined : oud.tot;
    zet('periode', van || tot ? { ...(van ? { van } : {}), ...(tot ? { tot } : {}) } : undefined);
  }
  // Via de paginafilter: stap-items, in huis, producten per proces en portfolio gaan naar pagina 1 (§7).
  zetPaginafilter(PROCESSEN_PAGINAFILTER, volgend);
}

/**
 * spec-tabelfilters story 12: de gedeelde Processen-filter in de lijststand `processen` (CAP-13, CAP-14).
 * Eén lezer per scherm: het adres gaat met de eigen lijsten van het scherm door `useStartadres`. De waarden
 * gelden al in de eerste render (startfilter vóór de store), zodat de eerste vraag gefilterd is.
 */
export function useProcessenFilter(eigenLijsten: readonly StartadresLijst[]) {
  const { start, geweigerd, wisGeweigerd } = useStartadres([...eigenLijsten, GEDEELD]);
  const bewaard = useLijstStandStore((s) => s.standen[PROCESSEN_PAGINAFILTER]?.filters);
  const filters = start[PROCESSEN_PAGINAFILTER]?.filters ?? bewaard;
  const values = useMemo(() => processenFilterWaarden(filters), [filters]);

  /** "Filters wissen": de Processen-stand en de linkmelding; de lijststand van het scherm wist de pagina zelf. */
  const wis = useCallback(() => {
    useLijstStandStore.getState().zetPaginafilter(PROCESSEN_PAGINAFILTER, {});
    wisGeweigerd();
  }, [wisGeweigerd]);

  return {
    values,
    setFilters: schrijf,
    wis,
    start,
    geweigerd,
    wisGeweigerd,
    effectiveStilstandDrempelWerkdagen: values.stilstandDrempelWerkdagen ?? DEFAULT_STILSTAND_DREMPEL_WERKDAGEN,
    defaultStilstandDrempelWerkdagen: DEFAULT_STILSTAND_DREMPEL_WERKDAGEN,
  };
}

